import { isOptionHiddenByRule, isOptionValueHiddenByRule } from './businessRules';
import type { Fabric, HiddenOptionsAndValues, OptionsSelection, typeOptionValue } from './interfaces';
import type { ConfigurationTypes, ProductType } from './productDefinition';
import { getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId } from './productDefinition';
import { calculateFabricPrice, isAdditonalProduct } from './utils';

export const calculateTotalPrice = ({
  product,
  selectedFabric,
  selectedOptions,
  hiddenOptionsAndValues,
  optionValues,
  isAdditionalProductAdded,
}: {
  product: ProductType | undefined;
  selectedFabric: Fabric | null;
  selectedOptions: OptionsSelection;
  hiddenOptionsAndValues: HiddenOptionsAndValues;
  optionValues: typeOptionValue;
  isAdditionalProductAdded: Record<string, boolean>;
}): number => {
  if (!selectedFabric || !selectedOptions || !hiddenOptionsAndValues || !optionValues || !product) {
    return 0;
  }

  let totalPrice = calculateFabricPrice(product, selectedFabric.prices?.configurationTypes);

  Object.entries(selectedOptions).forEach(([configurationTypeId, configurationSelectedOptions]) => {
    const configurationType = configurationTypeId as ConfigurationTypes;
    const priceShouldApply =
      !isAdditonalProduct(product, configurationType) || isAdditionalProductAdded[configurationTypeId];

    Object.entries(configurationSelectedOptions).forEach(([optionId, optionValueId]) => {
      const additionalPrice = optionValues[optionValueId]?.additionalPrice?.amount;
      if (
        additionalPrice &&
        priceShouldApply &&
        !isOptionValueHiddenByRule(optionValueId, hiddenOptionsAndValues, configurationType) &&
        !isOptionHiddenByRule(+optionId, hiddenOptionsAndValues, configurationType)
      ) {
        totalPrice += additionalPrice;
      }
    });
  });

  Object.keys(isAdditionalProductAdded).forEach(configurationTypeId => {
    if (isAdditionalProductAdded[configurationTypeId]) {
      // Extra configurations carry their own id but are priced with the base product's definition.
      const baseConfigurationTypeId =
        getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+configurationTypeId);
      const additionalPrice = selectedFabric.prices?.configurationTypes[+baseConfigurationTypeId];
      if (additionalPrice) {
        totalPrice += additionalPrice;
      }
    }
  });

  return totalPrice;
};
